<template>
  <div>
    <p-loading v-if="isLoading" />
    <form v-else :class="{ dirty: $v.$anyDirty }" autocomplete="off" class="create-or-edit-form" @submit.prevent="submit">
      <div class="form-row">
        <p-text-field v-model="name" :label="'Name'" autocomplete="off" />
        <ul v-if="$v.name">
          <li v-if="!$v.name.required" class="error">Name is required</li>
        </ul>
      </div>
      <div class="form-row">
        <p-text-field v-model="description" :label="'Description'" autocomplete="off" />
        <ul v-if="$v.description">
          <li v-if="!$v.description.required" class="error">Description is required</li>
        </ul>
      </div>
      <div v-if="!item || formId !== null" class="form-row">
        <p-multiselect v-model="formId" :options="formsOptions" :get-label-callback="getFormName" label="Form" placeholder="Select form" />
      </div>
      <div class="form-row">
        <p-multiselect
          v-model="organizations"
          :multiple="true"
          :searchable="true"
          :options="organizationsOptions"
          :get-label-callback="getOrganizationName"
          label="Organizations"
          placeholder="Select Organizations"
        />
      </div>
      <div class="form-row">
        <p-multiselect v-model="tags" :multiple="true" :taggable="true" label="Tags" placeholder="" tag-placeholder="" @tag="tagAdded" />
      </div>
      <div class="form-row">
        <p-checkbox v-model="hideInReports" label="Hide in Reports" />
      </div>
      <div v-if="formId" class="form-row">
        <p-checkbox v-model="requiresContext" label="Enable Context Document Upload" />
        <div v-if="requiresContext" class="context-field-group">
          <div class="field-row">
            <div>
              <p-text-field :value="contextFieldDisplayName" :label="'Field Name'" autocomplete="off" disabled />
            </div>
            <div>
              <p-text-field v-if="contextFieldIndex !== -1" v-model="fields[contextFieldIndex].label" :label="'Field Label'" autocomplete="off" />
              <p-text-field v-else :value="contextFieldDisplayName" :label="'Field Label'" autocomplete="off" disabled />
              <ul v-if="contextFieldValidation">
                <li v-if="!contextFieldValidation.label.required" class="error">Field Label is required</li>
              </ul>
            </div>
          </div>
          <p-checkbox v-model="isContextRequired" label="Required" />
        </div>
      </div>
      <div v-if="editableFields.length" class="form-row">
        <div class="fields-grid">
          <div v-for="{ field, index } in editableFields" :key="field.id || field.name" class="field-row">
            <div>
              <p-text-field :value="field.name" :label="'Field Name'" autocomplete="off" disabled />
            </div>
            <div>
              <p-text-field v-model="fields[index].label" :label="'Field Label'" autocomplete="off" />
              <ul v-if="$v.fields && $v.fields.$each && $v.fields.$each[index] && $v.fields.$each[index].label">
                <li v-if="!$v.fields.$each[index].label.required" class="error">Field Label is required</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="form-row submit">
        <p-button type="button" @click.prevent="$router.back()">Cancel</p-button>
        <p-button color="primary" type="submit">Save</p-button>
      </div>
    </form>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

import Loading from '@/components/common/Loading';
import TextField from '@/components/common/TextField';
import Button from '@/components/common/Button';
import Multiselect from '@/components/common/Multiselect';
import Checkbox from '@/components/common/Checkbox';
import { mapState } from 'vuex';

// Matches `contextVariableName` in the reports service; the backend seeds this
// field on create and looks it up by name, so it is never editable here.
const CONTEXT_FIELD_NAME = 'Context';
const CONTEXT_FIELD_DISPLAY_NAME = 'Document';

export default {
  components: {
    'p-loading': Loading,
    'p-text-field': TextField,
    'p-button': Button,
    'p-multiselect': Multiselect,
    'p-checkbox': Checkbox
  },
  props: {
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    if (this.item) {
      return {
        name: this.item.name,
        description: this.item.description,
        organizations: this.item?.organizations ?? [],
        formId: this.item?.formId ?? null,
        requiresContext: this.item?.requiresContext ?? false,
        isContextOptional: this.item?.isContextOptional ?? true,
        hideInReports: this.item?.hideInReports ?? false,
        tags: this.item?.tags ?? [],
        fields: (this.item.fields || []).map(f => ({ ...f }))
      };
    }
    return {
      name: '',
      description: '',
      organizations: [],
      formId: null,
      requiresContext: false,
      isContextOptional: true,
      hideInReports: false,
      tags: [],
      fields: []
    };
  },
  computed: {
    ...mapState({
      organizationsCollection: s => s.organizations.collection,
      formsCollection: s => s.forms.collection,
      organizationsLoading: s => s.organizations.isGetCollectionRequestPending,
      formsLoading: s => s.forms.isGetCollectionRequestPending,
      reportLoading: s => s.reports.isGetRequestPending
    }),
    isLoading() {
      return this.organizationsLoading || this.formsLoading || this.reportLoading;
    },
    organizationsOptions() {
      return this.organizationsCollection?.map(o => o.id) || [];
    },
    formsOptions() {
      return this.formsCollection?.map(f => f.id) || [];
    },
    contextFieldDisplayName() {
      return CONTEXT_FIELD_DISPLAY_NAME;
    },
    contextFieldIndex() {
      return this.fields.findIndex(f => f.name === CONTEXT_FIELD_NAME);
    },
    editableFields() {
      return this.fields.map((field, index) => ({ field, index })).filter(({ field }) => field.name !== CONTEXT_FIELD_NAME);
    },
    contextFieldValidation() {
      const index = this.contextFieldIndex;
      if (index === -1) {
        return null;
      }

      return this.$v.fields?.$each?.[index]?.label ? this.$v.fields.$each[index] : null;
    },
    contextFieldPreviewLabel() {
      const label = (this.contextFieldIndex === -1 ? '' : this.fields[this.contextFieldIndex].label) || CONTEXT_FIELD_DISPLAY_NAME;

      return this.isContextRequired ? label : `${label} (Optional)`;
    },
    isContextRequired: {
      get() {
        return !this.isContextOptional;
      },
      set(value) {
        this.isContextOptional = !value;
      }
    }
  },
  watch: {
    item: {
      handler(newItem) {
        if (newItem) {
          this.name = newItem.name;
          this.description = newItem.description;
          this.organizations = newItem.organizations || [];
          this.formId = newItem?.formId ?? null;
          this.requiresContext = newItem?.requiresContext ?? false;
          this.isContextOptional = newItem?.isContextOptional ?? true;
          this.hideInReports = newItem?.hideInReports ?? false;
          this.tags = newItem?.tags ?? [];
          this.fields = (newItem.fields || []).map(f => ({ ...f }));
        }
      },
      immediate: true
    },
    formId(newFormId) {
      if (!newFormId) {
        this.requiresContext = false;
      }
    },
    requiresContext(newRequiresContext) {
      if (!newRequiresContext) {
        this.isContextOptional = true;
      }
    }
  },
  validations() {
    return {
      name: {
        required
      },
      description: {
        required
      },
      organizations: {},
      fields: {
        $each: {
          label: {
            required
          }
        }
      }
    };
  },

  async created() {
    await Promise.all([this.$store.dispatch('organizations/getCollection'), this.$store.dispatch('forms/getCollection')]);
  },
  methods: {
    getOrganizationName(value) {
      return this.organizationsCollection.find(o => o.id === value)?.name ?? 'Unknown Organization';
    },
    getFormName(value) {
      return this.formsCollection.find(f => f.id === value)?.name ?? 'Unknown Form';
    },
    organizationAdded(organization) {
      if (!Array.isArray(this.organizations)) {
        this.organizations = [];
      }

      if (this.organizations.includes(organization)) {
        return;
      }
      this.organizations.push(organization);
    },
    tagAdded(tag) {
      if (!Array.isArray(this.tags)) {
        this.tags = [];
      }

      if (this.tags.includes(tag)) {
        return;
      }
      this.tags.push(tag);
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.$emit('submit', {
        name: this.name,
        description: this.description,
        organizations: this.organizations,
        formId: this.formId,
        requiresContext: this.requiresContext,
        isContextOptional: this.requiresContext ? this.isContextOptional : true,
        hideInReports: this.hideInReports,
        tags: this.tags,
        fields: this.fields.map(f => ({ id: f.id, label: f.label }))
      });

      this.$v.$reset();
    }
  }
};
</script>

<style lang="scss" scoped>
.create-or-edit-form {
  padding: 2rem;
  max-width: 480px;
  background: var(--theme-surface);
  overflow-y: auto;

  .organizations {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 30px;
  }

  > * {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
    &:last-child {
      display: flex;
      justify-content: flex-end;
    }
  }

  .error {
    font-size: 0.8rem;
    color: var(--theme-error);
    text-align: left;
    padding: 0.25rem 0;
    display: none;
  }
  &.dirty {
    .error {
      display: block;
    }
  }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .fields-grid {
    display: grid;
    gap: 0.75rem;
  }

  .context-field-group {
    display: grid;
    gap: 0.75rem;
    margin-top: 0.75rem;
    margin-left: 1.25rem;
  }

  .context-field-preview {
    font-size: 0.75rem;
    color: var(--theme-on-surface);
    opacity: 0.7;
  }
}
</style>
